import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

// Audio is disabled by default to prevent CORS/loading errors
// Users can enable audio in settings if they want to try loading external sounds
const AUDIO_ENABLED = false;

// Audio URLs - External sources may be blocked by CORS in some browsers
// These are optional enhancements and the app works fully without them
const AUDIO_SOURCES = AUDIO_ENABLED ? {
  music: 'https://assets.mixkit.co/music/preview/mixkit-spirit-in-the-woods-139.mp3',
  sfx: {
    cardFlip: 'https://assets.mixkit.co/active_storage/sfx/2073/2073-preview.mp3',
    buttonHover: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3',
    cardReveal: 'https://assets.mixkit.co/active_storage/sfx/1111/1111-preview.mp3',
    navigation: 'https://assets.mixkit.co/active_storage/sfx/2570/2570-preview.mp3',
    success: 'https://assets.mixkit.co/active_storage/sfx/2018/2018-preview.mp3',
    mystical: 'https://assets.mixkit.co/active_storage/sfx/2572/2572-preview.mp3',
    click: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3'
  }
} : { music: null, sfx: {} };

const AudioContext = createContext(null);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    musicEnabled: false,
    sfxEnabled: true,
    musicVolume: 0.3,
    sfxVolume: 0.5
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const musicRef = useRef(null);
  const sfxRefs = useRef({});
  
  // Load settings from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('audioSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
      } catch (e) {
        console.error('Failed to parse audio settings');
      }
    }
  }, []);
  
  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('audioSettings', JSON.stringify(settings));
  }, [settings]);
  
  // Initialize audio elements (only if audio is enabled)
  useEffect(() => {
    if (!AUDIO_ENABLED || !AUDIO_SOURCES.music) {
      setIsLoaded(true);
      return;
    }
    
    try {
      // Create music element
      musicRef.current = new Audio(AUDIO_SOURCES.music);
      musicRef.current.loop = true;
      musicRef.current.volume = settings.musicVolume;
      
      // Add error handler to prevent console spam
      musicRef.current.onerror = () => {
        // Silently handle - audio is optional
      };
      
      // Preload SFX with error handling
      Object.entries(AUDIO_SOURCES.sfx).forEach(([key, url]) => {
        if (!url) return;
        const audio = new Audio(url);
        audio.preload = 'auto';
        audio.volume = settings.sfxVolume;
        audio.onerror = () => {
          // Silently handle - audio is optional
        };
        sfxRefs.current[key] = audio;
      });
    } catch (e) {
      // Audio initialization failed - continue without audio
    }
    
    setIsLoaded(true);
    
    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current = null;
      }
    };
  }, []);
  
  // Update volumes when settings change
  useEffect(() => {
    if (musicRef.current) {
      musicRef.current.volume = settings.musicVolume;
    }
    Object.values(sfxRefs.current).forEach(audio => {
      audio.volume = settings.sfxVolume;
    });
  }, [settings.musicVolume, settings.sfxVolume]);
  
  // Handle music playback (only if audio is enabled)
  useEffect(() => {
    if (!AUDIO_ENABLED || !musicRef.current) return;
    
    if (settings.musicEnabled && !isPlaying) {
      musicRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Music autoplay blocked or failed - silent fail
      });
    } else if (!settings.musicEnabled && isPlaying) {
      musicRef.current.pause();
      setIsPlaying(false);
    }
  }, [settings.musicEnabled, isPlaying]);
  
  const toggleMusic = useCallback(() => {
    setSettings(prev => ({ ...prev, musicEnabled: !prev.musicEnabled }));
  }, []);
  
  const toggleSfx = useCallback(() => {
    setSettings(prev => ({ ...prev, sfxEnabled: !prev.sfxEnabled }));
  }, []);
  
  const setMusicVolume = useCallback((volume) => {
    setSettings(prev => ({ ...prev, musicVolume: volume }));
  }, []);
  
  const setSfxVolume = useCallback((volume) => {
    setSettings(prev => ({ ...prev, sfxVolume: volume }));
  }, []);
  
  const playSfx = useCallback((type) => {
    if (!AUDIO_ENABLED || !settings.sfxEnabled || !sfxRefs.current[type]) return;
    
    try {
      const audio = sfxRefs.current[type];
      audio.currentTime = 0;
      audio.play().catch(() => {
        // Silently fail - audio is optional enhancement
      });
    } catch (e) {
      // Silently handle errors
    }
  }, [settings.sfxEnabled]);
  
  // Convenience methods for specific sounds
  const playCardFlip = useCallback(() => playSfx('cardFlip'), [playSfx]);
  const playCardReveal = useCallback(() => playSfx('cardReveal'), [playSfx]);
  const playButtonHover = useCallback(() => playSfx('buttonHover'), [playSfx]);
  const playNavigation = useCallback(() => playSfx('navigation'), [playSfx]);
  const playSuccess = useCallback(() => playSfx('success'), [playSfx]);
  const playMystical = useCallback(() => playSfx('mystical'), [playSfx]);
  const playClick = useCallback(() => playSfx('click'), [playSfx]);
  
  const value = {
    settings,
    setSettings,
    isPlaying,
    isLoaded,
    toggleMusic,
    toggleSfx,
    setMusicVolume,
    setSfxVolume,
    playSfx,
    playCardFlip,
    playCardReveal,
    playButtonHover,
    playNavigation,
    playSuccess,
    playMystical,
    playClick
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

export default AudioProvider;
